import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Crown, Users, Compass, Briefcase, Building2, Globe2, CheckCircle2, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/Button';
const practices = [{
  num: '01',
  icon: Crown,
  title: 'Executive Search',
  tagline: 'CEO, C-Suite & Senior Leadership',
  desc: 'Confidential, retained-search mandates for the most consequential hires. We partner with boards, investors, and CEOs to identify, assess, and secure the transformative leaders organizations need to navigate their next chapter.',
  capabilities: ['CEO succession & external recruitment', 'C-suite searches (CFO, CHRO, CTO, COO, CMO, CRO)', 'PE & VC portfolio leadership', 'Confidential cross-border placements']
}, {
  num: '02',
  icon: Users,
  title: 'Board & Director Services',
  tagline: 'Independent Directors & Board Composition',
  desc: 'A dedicated practice for board appointments, governance advisory, and board composition strategy. We help organizations build boards that bring sector depth, functional balance, and diversity of perspective.',
  capabilities: ['Independent director searches', 'Board composition assessment', 'Audit, compensation & nominating committees', 'Board diversity advisory']
}, {
  num: '03',
  icon: Compass,
  title: 'Leadership Advisory',
  tagline: 'Succession, Assessment & Onboarding',
  desc: 'Beyond placement, we advise organizations on the strategic leadership decisions that shape long-term performance — from CEO succession planning to executive assessment to onboarding integration.',
  capabilities: ['CEO & C-suite succession planning', 'Executive assessment & benchmarking', 'Leadership team effectiveness reviews', 'New-leader integration & onboarding']
}, {
  num: '04',
  icon: Briefcase,
  title: 'Resume & Career Positioning',
  tagline: 'Executive-Level Personal Branding',
  desc: 'A specialist offering for senior executives navigating their own next move. We provide strategic positioning, executive biography development, and LinkedIn architecture for leaders who require the highest level of discretion.',
  capabilities: ['Executive resume & biography development', 'LinkedIn architecture for C-suite leaders', 'Board appointment positioning', 'Confidential career strategy sessions']
}, {
  num: '05',
  icon: Building2,
  title: 'Talent Acquisition Consulting',
  tagline: 'Enterprise TA Strategy & Function Design',
  desc: 'For organizations building or scaling internal talent acquisition capability, we advise on function design, employer branding, recruitment technology, and high-volume hiring strategy at the enterprise level.',
  capabilities: ['In-house TA function design', 'Recruitment process optimization', 'Employer brand strategy', 'Hiring manager enablement']
}, {
  num: '06',
  icon: Globe2,
  title: 'Global Mobility & Cross-Border',
  tagline: 'International Placement & Relocation',
  desc: 'A practice dedicated to cross-border executive moves. We handle the complexity of multi-jurisdiction placements — including work authorization advisory, cultural integration, and relocation support.',
  capabilities: ['Cross-border executive placement', 'Work authorization & visa advisory', 'Cultural & market briefings', 'Spouse & family relocation support']
}];
const engagementModels = [{
  name: 'Retained Search',
  desc: 'Exclusive, retained partnership for C-suite, board, and senior leadership mandates. Our flagship engagement model.',
  fee: 'Tiered retainer + completion fee',
  timeline: '90–120 days',
  best: 'CEO, C-suite, board, sensitive cross-border'
}, {
  name: 'Engaged Search',
  desc: 'Mid-flexibility engagement for senior leadership roles below the C-suite. Combines rigor of retained with broader access.',
  fee: 'Partial retainer + placement fee',
  timeline: '60–90 days',
  best: 'VP, SVP, and senior director roles'
}, {
  name: 'Advisory Engagement',
  desc: 'Project-based consulting for succession, assessment, and leadership-team advisory work — independent of any search.',
  fee: 'Fixed project fee',
  timeline: '30–90 days',
  best: 'Succession planning, assessment, governance'
}];
export function ServicesPage() {
  return <div className="bg-white" data-id="element-842" data-mp="30i83bwgs3xkm">
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="relative bg-navy-900 text-white pt-32 pb-24 overflow-hidden" data-id="element-843" data-mp="35ht7clat2w47">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" data-id="element-844" data-mp="3acgsca6lp8e1" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12" data-id="element-845" data-mp="2htjq7wojewje">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end" data-id="element-846" data-mp="2hrqi0cya049a">
            <div className="lg:col-span-8" data-id="element-847" data-mp="yy5msrfrldrm">
              <div className="flex items-center gap-3 mb-8" data-id="element-848" data-mp="uwtycp8mmyl0">
                <div className="w-12 h-px bg-gold-500" data-id="element-849" data-mp="uv8zwoj6yvhv" />
                <span className="text-gold-500 font-semibold tracking-[0.3em] uppercase text-xs" data-id="element-850" data-mp="px7kqeovzgbo">
                  Practice Areas
                </span>
              </div>
              <motion.h1 initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8
            }} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold leading-[1.05] mb-8 tracking-tight" data-id="element-851" data-mp="fzukijn6m9i3">
                Advisory built around the{' '}
                <span className="italic text-gold-500" data-id="element-852" data-mp="3ofwf2tpdgwbx">most consequential</span>{' '}
                leadership hires.
              </motion.h1>
            </div>
            <div className="lg:col-span-4" data-id="element-853" data-mp="3eqz4dd0gxp5i">
              <p className="text-xl text-gray-300 leading-relaxed font-light" data-id="element-854" data-mp="3jr3pxhg8ha2l">
                Six integrated practice areas. One commitment: helping you
                identify and secure the leaders who define your organization's
                next decade.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRACTICES — Detailed Cards ───────────────────────── */}
      <section className="py-28 bg-white" data-id="element-855" data-mp="6cqisowd5ymu">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-856" data-mp="6bp5sevsf22h">
          <div className="space-y-px bg-gray-200" data-id="element-857" data-mp="3or9hkxmi6wko">
            {practices.map((practice, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-white p-10 md:p-14 group hover:bg-gray-50 transition-colors duration-500" data-id="element-858" data-mp="3sr058sgqgwkz" data-mpi={index}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-id="element-859" data-mp="2jl9kiagb4b3n">
                  {/* Number + Icon */}
                  <div className="lg:col-span-2" data-id="element-860" data-mp="1quj97uuensld">
                    <div className="flex lg:flex-col items-center lg:items-start gap-4" data-id="element-861" data-mp="1qvauabqdm7xe">
                      <span className="text-5xl font-serif text-gold-500" data-id="element-862" data-mp="1lwhoz6l8tn8b">
                        {practice.num}
                      </span>
                      <practice.icon className="h-8 w-8 text-navy-900 lg:mt-4" data-id="element-863" data-mp="20qmbr1t65t8u" />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="lg:col-span-7" data-id="element-864" data-mp="3c6jafhqt7rj9">
                    <div className="text-xs uppercase tracking-[0.25em] text-gold-600 font-semibold mb-3" data-id="element-865" data-mp="378rkgt9q6fhp">
                      {practice.tagline}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 leading-tight mb-5" data-id="element-866" data-mp="14nx79sxocnwf">
                      {practice.title}
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed" data-id="element-867" data-mp="1eji6xkf9tc30">
                      {practice.desc}
                    </p>
                  </div>

                  {/* Capabilities */}
                  <div className="lg:col-span-3 lg:border-l lg:border-gray-200 lg:pl-8" data-id="element-868" data-mp="1o9bi4kbi3p6q">
                    <div className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-4" data-id="element-869" data-mp="1sancf35bkyl9">
                      Capabilities
                    </div>
                    <ul className="space-y-3" data-id="element-870" data-mp="229ma5qojr9th">
                      {practice.capabilities.map((cap, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-871" data-mp="2x9zipj5628j" data-mpi={i}>
                          <CheckCircle2 className="h-4 w-4 text-gold-500 mt-0.5 flex-shrink-0" data-id="element-872" data-mp="cc1jbgkwm3t" />
                          <span data-id="element-873" data-mp="e570nfgh0a8">{cap}</span>
                        </li>)}
                    </ul>
                    <a href="/contact" className="inline-flex items-center gap-2 mt-6 text-navy-900 font-semibold text-sm hover:text-gold-600 transition-colors group/link" data-id="element-874" data-mp="3mrn9kdflboxx">
                      Discuss this practice
                      <ArrowUpRight className="h-4 w-4 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" data-id="element-875" data-mp="ec3mjw5ij2rb" />
                    </a>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ─── SECTOR EXPERTISE STRIP ───────────────────────────── */}
      <section className="py-20 bg-navy-900 text-white" data-id="element-876" data-mp="2u4j1u0p44wig">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-877" data-mp="2p3vd5locwrre">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-12" data-id="element-878" data-mp="2p6hjxj3fb38k">
            <div className="lg:col-span-5" data-id="element-879" data-mp="2w2zek11xaqzf">
              <div className="flex items-center gap-3 mb-6" data-id="element-880" data-mp="2r6haz4ibwyoo">
                <div className="w-12 h-px bg-gold-500" data-id="element-881" data-mp="2r3wbgm5i6uu3" />
                <span className="text-gold-500 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-882" data-mp="35vwz0cqh6q0o">
                  Sector Depth
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold leading-tight" data-id="element-883" data-mp="3ev0llqoupnjx">
                Every practice, led by sector specialists.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7" data-id="element-884" data-mp="3otprsm7dxjkc">
              <p className="text-gray-300 leading-relaxed text-lg" data-id="element-885" data-mp="3ot7pb7u4q93z">
                Our consultants bring operating experience in the industries
                they serve. They speak the language, know the talent, and
                understand the strategic forces shaping each sector.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10" data-id="element-886" data-mp="4ykwagb1zfie">
            {['Technology', 'Financial Services', 'Healthcare & Life Sciences', 'Industrial & Manufacturing', 'Aerospace & Defense', 'AI & Emerging Tech', 'Energy & Sustainability', 'Consumer & Retail', 'Professional Services', 'Private Equity', 'Government & Public Sector', 'Real Estate'].map((sector, i) => <div key={i} className="bg-navy-900 p-5 hover:bg-navy-800 transition-colors text-sm font-medium tracking-tight" data-id="element-887" data-mp="3na7qihuuaqit" data-mpi={i}>
                {sector}
              </div>)}
          </div>
        </div>
      </section>

      {/* ─── ENGAGEMENT MODELS ────────────────────────────────── */}
      <section className="py-28 bg-gray-50" data-id="element-888" data-mp="1blymgdov58uv">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-889" data-mp="1bknjgg38s54t">
          <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-890" data-mp="1a6njdnieju45">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-891" data-mp="15717pii82lxg">
              <div className="w-12 h-px bg-gold-500" data-id="element-892" data-mp="159436i25979k" />
              <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-893" data-mp="108g3z93rhk49">
                Engagement Models
              </span>
              <div className="w-12 h-px bg-gold-500" data-id="element-894" data-mp="1swc8gzzkogbl" />
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight mb-6" data-id="element-895" data-mp="1svkbbkqbuaj7">
              Three ways to engage our firm.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-896" data-mp="1j5hc32sm40cf">
              We tailor the engagement model to the consequence of the mandate.
              Pricing is transparent and structured around outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200" data-id="element-897" data-mp="2booatmq87hlq">
            {engagementModels.map((model, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-10" data-id="element-898" data-mp="3pjehdpo39pbd" data-mpi={i}>
                <h3 className="text-2xl font-serif font-bold text-navy-900 mb-3" data-id="element-899" data-mp="hbfyg4ryx8rr">
                  {model.name}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8 text-sm" data-id="element-900" data-mp="m1wcnr3m4yf6">
                  {model.desc}
                </p>

                <div className="space-y-5 pt-6 border-t border-gray-100" data-id="element-901" data-mp="q38504dvfxr2">
                  <div data-id="element-902" data-mp="v3m4uz5bk1e0">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-1" data-id="element-903" data-mp="v4e0ub2tw14t">
                      Fee Structure
                    </div>
                    <div className="text-navy-900 font-semibold text-sm" data-id="element-904" data-mp="mex68hdpdjbt">
                      {model.fee}
                    </div>
                  </div>
                  <div data-id="element-905" data-mp="hfu4b5t0rt82">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-1" data-id="element-906" data-mp="w50ajdq9yjpg">
                      Typical Timeline
                    </div>
                    <div className="text-navy-900 font-semibold text-sm" data-id="element-907" data-mp="r6fabeb0w010">
                      {model.timeline}
                    </div>
                  </div>
                  <div data-id="element-908" data-mp="3un5k849q6x7z">
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-1" data-id="element-909" data-mp="3unp0zhnn3fkf">
                      Best Suited For
                    </div>
                    <div className="text-navy-900 font-semibold text-sm" data-id="element-910" data-mp="8ffh20whw41x">
                      {model.best}
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <div className="text-center mt-12" data-id="element-911" data-mp="1jtj7wy7sdc2r">
            <p className="text-sm text-gray-500 italic" data-id="element-912" data-mp="1jwp0wojjcy3r">
              Unsure which model fits your mandate? We will help you scope it in
              a confidential conversation.
            </p>
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────── */}
      <section className="py-28 bg-white" data-id="element-913" data-mp="1mf0rc2z7jyq9">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center" data-id="element-914" data-mp="1mg21844hmf6e">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-915" data-mp="2a4uyq50rcv34">
            <div className="w-12 h-px bg-gold-500" data-id="element-916" data-mp="2a6xqathk6w9y" />
            <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-917" data-mp="2f2mtd6ssevb0">
              Begin a Conversation
            </span>
            <div className="w-12 h-px bg-gold-500" data-id="element-918" data-mp="21agmkbbiy0o8" />
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight mb-6" data-id="element-919" data-mp="25cbptld0mo4n">
            Let's scope your mandate.
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-920" data-mp="2xupeovgdizrl">
            Our senior consultants are available for a confidential,
            no-obligation scoping conversation. We will respond within one
            business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-921" data-mp="33mq854x87r84">
            <Button variant="gold" size="lg" href="mailto:contact@talentbridge.blog" data-id="element-922" data-mp="2ynn15exoraxi">
              Request a Consultation
              <ArrowRight className="ml-2 h-4 w-4" data-id="element-923" data-mp="2os3jnxjvcesa" />
            </Button>
            <Button variant="outline" size="lg" href="/how-it-works" data-id="element-924" data-mp="2k01xnjwwf9p2">
              See Our Process
            </Button>
          </div>
        </div>
      </section>
    </div>;
}